import { useLang } from "@/i18n/LanguageContext";

// Compact, OS-style decision flow. Reads left-to-right on desktop, top-to-
// bottom on mobile. Each node is a small "system module" with its own
// tag, label, and inline chip strip - resembling a control-panel rather
// than a generic consulting flow.
const NODES = [
  {
    id: "signals",
    tag: "01",
    label: { en: "Signals", fr: "Signaux" },
    hint: { en: "Data in", fr: "Données entrantes" },
    items: ["Demand", "Supply", "Inventory", "Capacity", "Constraints"],
    color: "border-[#00A878]",
  },
  {
    id: "logic",
    tag: "02",
    label: { en: "Decision logic", fr: "Logique de décision" },
    hint: { en: "Compute", fr: "Calcul" },
    items: ["Planning", "Rules", "Optimization", "AI", "Judgment"],
    color: "border-[#00A878]",
  },
  {
    id: "experience",
    tag: "03",
    label: { en: "Decision experience", fr: "Expérience de décision" },
    hint: { en: "Interface", fr: "Interface" },
    items: ["APS", "Decision App", "Workflow", "Dashboard", "Agent"],
    color: "border-[#00A878]",
  },
  {
    id: "action",
    tag: "04",
    label: { en: "Action", fr: "Action" },
    hint: { en: "Execute", fr: "Exécuter" },
    items: [],
    color: "border-[#00A878]",
    highlight: true,
  },
  {
    id: "feedback",
    tag: "05",
    label: { en: "Feedback", fr: "Feedback" },
    hint: { en: "Learn", fr: "Apprendre" },
    items: [],
    color: "border-[#00A878]",
  },
];

const Node = ({ n, lang, isLast }) => (
  <div
    data-testid={`ds-node-${n.id}`}
    className={`relative flex-1 min-w-0 ${n.highlight ? "bg-[#00A878] text-white" : "bg-[#0f172a] text-white"} border ${n.highlight ? "border-[#00A878]" : "border-[#1F2937]"} p-4 md:p-5`}
  >
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">{n.tag}</span>
      <span className="font-mono-dl text-[9px] tracking-widest uppercase text-[#9ca3af]">{lang === "fr" ? n.hint.fr : n.hint.en}</span>
    </div>
    <div className="font-display text-[17px] mt-1.5 leading-tight">
      {lang === "fr" ? n.label.fr : n.label.en}
    </div>
    {n.items.length > 0 && (
      <div className="mt-3 flex flex-wrap gap-1">
        {n.items.map((it) => (
          <span key={it} className="text-[10px] font-mono-dl tracking-wider text-white/80 bg-white/5 border border-white/10 px-1.5 py-0.5">
            {it}
          </span>
        ))}
      </div>
    )}
    {/* Connector to next node (desktop = right arrow, mobile = down arrow) */}
    {!isLast && (
      <>
        <div className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 w-3 h-px bg-[#00A878]" aria-hidden />
        <div className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 text-[#00A878] text-[10px]" aria-hidden>›</div>
        <div className="md:hidden flex justify-center py-1 text-[#00A878]" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v11M2 8l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </div>
      </>
    )}
  </div>
);

const DecisionSystemVisual = () => {
  const { lang } = useLang();
  return (
    <div data-testid="decision-system-visual" className="grid lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-8 items-start">
      <div>
        <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">
          {lang === "fr" ? "Système de décision" : "Decision system"}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 text-white leading-tight">
          {lang === "fr" ? "Un flux, pas un empilement." : "A flow, not a stack."}
        </h2>
        <p className="mt-4 text-[14px] text-[#9ca3af] max-w-sm leading-relaxed">
          {lang === "fr"
            ? "APS, ERP, PLM, IA, agents - la valeur n'est pas dans l'outil. Elle est dans le flux de décision qui les relie."
            : "APS, ERP, PLM, AI, agents - the value is not in the tool. It's in the decision flow that connects them."}
        </p>
        <div className="mt-6 grid grid-cols-3 gap-2 max-w-xs">
          <div className="border border-[#1F2937] px-2 py-1.5 text-center">
            <div className="font-mono-dl text-[9px] tracking-wider text-[#00A878] uppercase">Nodes</div>
            <div className="font-display text-lg text-white">05</div>
          </div>
          <div className="border border-[#1F2937] px-2 py-1.5 text-center">
            <div className="font-mono-dl text-[9px] tracking-wider text-[#00A878] uppercase">Loops</div>
            <div className="font-display text-lg text-white">01</div>
          </div>
          <div className="border border-[#1F2937] px-2 py-1.5 text-center">
            <div className="font-mono-dl text-[9px] tracking-wider text-[#00A878] uppercase">Owner</div>
            <div className="font-display text-lg text-white">You</div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-col md:flex-row md:items-stretch gap-2 md:gap-3">
          {NODES.map((n, i) => <Node key={n.id} n={n} lang={lang} isLast={i === NODES.length - 1} />)}
        </div>
        {/* Feedback loop indicator */}
        <div className="mt-3 relative border-t border-dashed border-[#00A878]/40 pt-3">
          <span className="absolute left-1/2 -top-2.5 -translate-x-1/2 bg-[#111827] px-3 font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">
            ↺ {lang === "fr" ? "Boucle d'apprentissage" : "Learning loop"}
          </span>
          <div className="text-center text-[11px] font-mono-dl text-[#9ca3af]">
            {lang === "fr"
              ? "Chaque décision alimente la suivante."
              : "Every decision feeds the next one."}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionSystemVisual;
